import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ConfirmDialog from '../ConfirmDialog';
import { apiClient } from '../../../lib/apiClient';
import { formatVersionDt } from '../commonFunc';

export type VersionHistorySidebarProps = {
  open: boolean;
  wrId?: number | null;
  onClose: () => void;
  title?: string;
  width?: any;
  minWidth?: number;
  fetcher?: (wrId: number) => Promise<any>;
  renderContent?: (data: any) => React.ReactNode;
};

const defaultFetcher = async (wrId: number) => {
  const resp = await apiClient.get('/weekly_report_versions', { params: { wr_id: wrId } });
  return resp.data;
};

const VersionHistorySidebar: React.FC<VersionHistorySidebarProps> = ({
  open,
  wrId,
  onClose,
  title = 'バージョン履歴',
  width = { xs: '80vw', sm: '60vw', md: '45%', lg: 560 },
  minWidth = 420,
  fetcher = defaultFetcher,
  renderContent,
}) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [target, setTarget] = useState<{ wr_id: number; version: number } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // 展開状態管理: key は "wrId-version-subVersion"
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // リサイズ用状態とヘルパー
  const [widthPx, setWidthPx] = useState<number | null>(null);
  const draggingRef = React.useRef(false);
  const dragStartXRef = React.useRef(0);
  const startWidthRef = React.useRef(0);
  const moveHandlerRef = React.useRef<((e: MouseEvent) => void) | null>(null);
  const upHandlerRef = React.useRef<(() => void) | null>(null);
  const touchMoveHandlerRef = React.useRef<((e: TouchEvent) => void) | null>(null);
  const touchEndHandlerRef = React.useRef<(() => void) | null>(null);
  const sidebarRef = React.useRef<HTMLDivElement | null>(null);

  const cssSizeToPx = (val: any, vw: number, fallback: number) => {
    if (typeof val === 'number') return val;
    if (typeof val !== 'string') return fallback;
    const s = val.trim().toLowerCase();
    if (s.endsWith('px')) return parseFloat(s);
    if (s.endsWith('vw')) return (parseFloat(s) / 100) * vw;
    if (s.endsWith('%')) return (parseFloat(s) / 100) * vw;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : fallback;
  };

  const resolveWidthToPx = (w: any) => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const fallback = Number(minWidth) || 320;
    let v: any = w;
    if (w && typeof w === 'object') {
      // 簡易ブレークポイント解決（MUI準拠）
      const bp = vw >= 1536 ? 'xl' : vw >= 1200 ? 'lg' : vw >= 900 ? 'md' : vw >= 600 ? 'sm' : 'xs';
      v = (w as any)[bp] ?? (w as any).lg ?? (w as any).md ?? (w as any).sm ?? (w as any).xs;
    }
    return cssSizeToPx(v, vw, fallback);
  };

  // open/width 変更時に初期幅を解決
  useEffect(() => {
    if (!open) return;
    // 既にユーザ操作で確定済みの幅は維持
    if (widthPx != null) return;
    const base = resolveWidthToPx(width);
    const vw = window.innerWidth;
    const clamped = Math.max(minWidth, Math.min(base, vw - 100));
    setWidthPx(clamped);
  }, [open, width, minWidth, widthPx]);

  // widthPx のDOM反映（保険）
  useEffect(() => {
    if (sidebarRef.current && widthPx != null) {
      sidebarRef.current.style.width = `${widthPx}px`;
    }
  }, [widthPx]);

  // widthPx のDOM反映（保険）
  useEffect(() => {
    if (sidebarRef.current && widthPx != null) {
      sidebarRef.current.style.setProperty('width', `${widthPx}px`, 'important');
    }
  }, [widthPx]);

  // widthPx のDOM反映（保険）
  useEffect(() => {
    if (sidebarRef.current && widthPx != null) {
      sidebarRef.current.style.width = `${widthPx}px`;
    }
  }, [widthPx]);

  // ウィンドウリサイズ時にクランプ
  useEffect(() => {
    const onResize = () => {
      setWidthPx((prev) => {
        const current = (prev ?? resolveWidthToPx(width));
        return Math.max(minWidth, Math.min(current, window.innerWidth - 100));
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [width, minWidth]);

  // アンマウント時のクリーンアップ（ドラッグ中でも確実に解除）
  useEffect(() => {
    return () => {
      if (moveHandlerRef.current) window.removeEventListener('mousemove', moveHandlerRef.current);
      if (upHandlerRef.current) window.removeEventListener('mouseup', upHandlerRef.current);
      if (touchMoveHandlerRef.current) window.removeEventListener('touchmove', touchMoveHandlerRef.current);
      if (touchEndHandlerRef.current) window.removeEventListener('touchend', touchEndHandlerRef.current);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      draggingRef.current = false;
    };
  }, []);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    dragStartXRef.current = e.clientX;
    startWidthRef.current = (widthPx ?? resolveWidthToPx(width));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const dx = dragStartXRef.current - ev.clientX; // 左に引っ張ると拡大
      const next = startWidthRef.current + dx;
      const clamped = Math.max(minWidth, Math.min(next, window.innerWidth - 100));
      if (sidebarRef.current) sidebarRef.current.style.setProperty('width', `${clamped}px`, 'important');
      setWidthPx(clamped);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // ドロップ時点の最終幅をコミット
      const rect = sidebarRef.current?.getBoundingClientRect();
      if (rect) {
        const finalW = Math.max(minWidth, Math.min(rect.width, window.innerWidth - 100));
        setWidthPx(finalW);
      }
      if (moveHandlerRef.current) window.removeEventListener('mousemove', moveHandlerRef.current);
      if (upHandlerRef.current) window.removeEventListener('mouseup', upHandlerRef.current);
      moveHandlerRef.current = null;
      upHandlerRef.current = null;
    };

    moveHandlerRef.current = onMove;
    upHandlerRef.current = onUp;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startResizeTouch = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    dragStartXRef.current = e.touches[0].clientX;
    startWidthRef.current = (widthPx ?? resolveWidthToPx(width));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: TouchEvent) => {
      if (!draggingRef.current || ev.touches.length !== 1) return;
      const x = ev.touches[0].clientX;
      const dx = dragStartXRef.current - x;
      const next = startWidthRef.current + dx;
      const clamped = Math.max(minWidth, Math.min(next, window.innerWidth - 100));
      if (sidebarRef.current) sidebarRef.current.style.width = `${clamped}px`;
      setWidthPx(clamped);
    };
    const onEnd = () => {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // ドロップ時点の最終幅をコミット
      const rect = sidebarRef.current?.getBoundingClientRect();
      if (rect) {
        const finalW = Math.max(minWidth, Math.min(rect.width, window.innerWidth - 100));
        setWidthPx(finalW);
      }
      if (touchMoveHandlerRef.current) window.removeEventListener('touchmove', touchMoveHandlerRef.current);
      if (touchEndHandlerRef.current) window.removeEventListener('touchend', touchEndHandlerRef.current);
      touchMoveHandlerRef.current = null;
      touchEndHandlerRef.current = null;
    };

    touchMoveHandlerRef.current = onMove;
    touchEndHandlerRef.current = onEnd;
    window.addEventListener('touchmove', onMove, { passive: false } as any);
    window.addEventListener('touchend', onEnd);
  };

  // Pointer Events 対応（より確実にドラッグ検知）
  const startResizePointer = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    try { (e.currentTarget as any).setPointerCapture?.(e.pointerId); } catch {}
    draggingRef.current = true;
    dragStartXRef.current = e.clientX;
    startWidthRef.current = (widthPx ?? resolveWidthToPx(width));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const dx = dragStartXRef.current - e.clientX;
    const next = startWidthRef.current + dx;
    const clamped = Math.max(minWidth, Math.min(next, window.innerWidth - 100));
    setWidthPx(clamped);
  };

  const endResizePointer = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try { (e.currentTarget as any).releasePointerCapture?.(e.pointerId); } catch {}
  };
// ポインター統合ドラッグ（windowリスナーで確実に追従）
const pointerMoveHandlerRef = React.useRef<((e: PointerEvent) => void) | null>(null);
const pointerUpHandlerRef = React.useRef<((e: PointerEvent) => void) | null>(null);
// 追加: 統合ドラッグ用のマウス/タッチmoveハンドラー参照（クリーンアップ用）
const unifiedMouseMoveRef = React.useRef<((e: MouseEvent) => void) | null>(null);
const unifiedTouchMoveRef = React.useRef<((e: TouchEvent) => void) | null>(null);

const startResizeUnified = (e: React.PointerEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();
  try { (e.currentTarget as any).setPointerCapture?.(e.pointerId); } catch {}
  draggingRef.current = true;
  dragStartXRef.current = e.clientX;
  startWidthRef.current = (widthPx ?? resolveWidthToPx(width));
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  // Pointer move
  const onMovePtr = (ev: PointerEvent) => {
    if (!draggingRef.current) return;
    const dx = dragStartXRef.current - ev.clientX; // 左へ動かすと拡大
    const next = startWidthRef.current + dx;
    const clamped = Math.max(minWidth, Math.min(next, window.innerWidth - 100));
    if (sidebarRef.current) sidebarRef.current.style.setProperty('width', `${clamped}px`, 'important');
    setWidthPx(clamped);
  };

  // Mouse move fallback
  const onMoveMouse = (ev: MouseEvent) => {
    if (!draggingRef.current) return;
    const dx = dragStartXRef.current - ev.clientX;
    const next = startWidthRef.current + dx;
    const clamped = Math.max(minWidth, Math.min(next, window.innerWidth - 100));
    setWidthPx(clamped);
  };

  // Touch move fallback
  const onMoveTouch = (ev: TouchEvent) => {
    if (!draggingRef.current || ev.touches.length !== 1) return;
    const x = ev.touches[0].clientX;
    const dx = dragStartXRef.current - x;
    const next = startWidthRef.current + dx;
    const clamped = Math.max(minWidth, Math.min(next, window.innerWidth - 100));
    setWidthPx(clamped);
  };

  const onUp = () => {
    draggingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    // ドロップ時点の最終幅をコミット
    const rect = sidebarRef.current?.getBoundingClientRect();
    if (rect) {
      const finalW = Math.max(minWidth, Math.min(rect.width, window.innerWidth - 100));
      setWidthPx(finalW);
    }
    if (pointerMoveHandlerRef.current) window.removeEventListener('pointermove', pointerMoveHandlerRef.current);
    if (pointerUpHandlerRef.current) window.removeEventListener('pointerup', pointerUpHandlerRef.current);
    if (unifiedMouseMoveRef.current) window.removeEventListener('mousemove', unifiedMouseMoveRef.current);
    if (unifiedTouchMoveRef.current) window.removeEventListener('touchmove', unifiedTouchMoveRef.current as any);
    pointerMoveHandlerRef.current = null;
    pointerUpHandlerRef.current = null;
    unifiedMouseMoveRef.current = null;
    unifiedTouchMoveRef.current = null;
  };

  // register listeners (pointer + mouse + touch)
  pointerMoveHandlerRef.current = onMovePtr;
  pointerUpHandlerRef.current = onUp;
  unifiedMouseMoveRef.current = onMoveMouse;
  unifiedTouchMoveRef.current = onMoveTouch;
  window.addEventListener('pointermove', onMovePtr);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('mousemove', onMoveMouse);
  window.addEventListener('touchmove', onMoveTouch, { passive: false } as any);
};

// pointer イベントのクリーンアップ（アンマウント時）
useEffect(() => {
  return () => {
    if (pointerMoveHandlerRef.current) window.removeEventListener('pointermove', pointerMoveHandlerRef.current);
    if (pointerUpHandlerRef.current) window.removeEventListener('pointerup', pointerUpHandlerRef.current);
    if (unifiedMouseMoveRef.current) window.removeEventListener('mousemove', unifiedMouseMoveRef.current);
    if (unifiedTouchMoveRef.current) window.removeEventListener('touchmove', unifiedTouchMoveRef.current as any);
  };
}, []);

// サイドバー全体でも左端付近のポインターダウンを拾ってドラッグ開始（ハンドルが効かない場合のフォールバック）
const handleContainerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
  // MUI Dialog などが Portal で描画される場合でも、React のイベント伝播でここに届く。
  // そのまま座標だけで判定すると「サイドバー外」をクリックしただけでリサイズが開始され、
  // ボタン（キャンセル等）の click が潰れることがあるため、まず DOM 上の包含を確認する。
  if (!e.currentTarget.contains(e.target as Node)) return;
  const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
  const offsetX = e.clientX - rect.left;
  const threshold = 12; // 左端12px以内ならドラッグ開始
  if (offsetX >= 0 && offsetX <= threshold) {
    startResizeUnified(e);
  }
};

const makeKey = (it: any) =>
  `${Number(it?.wr_id) || 0}-${Number(it?.version) || 0}-${Number(it?.sub_version) || 0}`;


  const getLatestKey = (arr: any[]) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const m = new Map<string, any>();
    for (const it of arr as any[]) {
      const key = `${Number(it?.wr_id) || 0}|${Number(it?.version) || 0}|${Number(it?.sub_version) || 0}`;
      const prev = m.get(key);
      if (!prev) {
        m.set(key, it);
      } else {
        const tb = Date.parse(String(it?.version_dt ?? ''));
        const ta = Date.parse(String(prev?.version_dt ?? ''));
        const nb = Number.isNaN(tb) ? -Infinity : tb;
        const na = Number.isNaN(ta) ? -Infinity : ta;
        if (nb >= na) m.set(key, it);
      }
    }
    const list = Array.from(m.values()).sort((a: any, b: any) => {
      const byVer = Number(b?.version ?? 0) - Number(a?.version ?? 0);
      if (byVer !== 0) return byVer;
      const bySub = Number(b?.sub_version ?? 0) - Number(a?.sub_version ?? 0);
      if (bySub !== 0) return bySub;
      const tb = Date.parse(b?.version_dt ?? '');
      const ta = Date.parse(a?.version_dt ?? '');
      // 新しい日時を先に
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });
    return list.length ? makeKey(list[0]) : null;
  };

  // データ切り替え毎に「最新のみ展開、他は閉じる」
  useEffect(() => {
    if (!open) return;
    if (Array.isArray(data)) {
      const latest = getLatestKey(data);
      if (latest) setExpanded(new Set([latest]));
      else setExpanded(new Set());
    } else {
      setExpanded(new Set());
    }
  }, [open, data]);

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isExpanded = (it: any) => expanded.has(makeKey(it));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!open) return;
      const id = Number(wrId);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const result = await fetcher(id);
        if (!cancelled) setData(result);
      } catch (e: any) {
        const message =
          (e?.response?.data && (e.response.data.message || JSON.stringify(e.response.data))) ||
          e?.message ||
          '取得に失敗しました';
        if (!cancelled) setError(String(message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, wrId, fetcher, reloadKey]);

  const handleClickDelete = (wr_id?: number, version?: number) => {
    const id = Number(wr_id);
    const ver = Number(version);
    if (!Number.isFinite(id) || !Number.isFinite(ver)) return;
    setTarget({ wr_id: id, version: ver });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      await apiClient.post('/delete_wr_version', { wr_id: target.wr_id, version: target.version });
      setConfirmOpen(false);
      setTarget(null);
      setReloadKey((v) => v + 1);
    } catch (e) {
      console.error('バージョン削除に失敗:', e);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setTarget(null);
  };

  return (
    <Slide in={open} direction="left" mountOnEnter unmountOnExit timeout={{ enter: 250, exit: 200 }}>
      <Box
        ref={sidebarRef}
        className="wr-history-sidebar"
        onPointerDown={handleContainerPointerDown}
        style={{ width: `${(widthPx ?? resolveWidthToPx(width))}px` }}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          minWidth,
          bgcolor: isDark ? '#1e293b' : '#fff',
          borderLeft: isDark ? '1px solid #334155' : '1px solid #e0e0e0',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* リサイズハンドル（左端） */}
        <Box
          onPointerDown={startResizeUnified}
          onMouseDown={startResize}
          onTouchStart={startResizeTouch}
          role="separator"
          aria-orientation="vertical"
          onDoubleClick={() => setWidthPx(resolveWidthToPx(width))}
          sx={{
            position: 'absolute',
            top: 0,
            left: -4,
            width: 14,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 2000,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
            '&:active': { bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)' },
            touchAction: 'none',
            pointerEvents: 'auto',
          }}
          aria-label="サイズ変更ハンドル"
          title="ドラッグして横幅を変更"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: isDark ? '1px solid #334155' : '1px solid #eee',
            bgcolor: isDark ? '#0f172a' : '#fafafa',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">読み込み中…</Typography>
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          ) : Array.isArray(data) && data.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              バージョン履歴がありません
            </Typography>
          ) : data ? (
            renderContent ? (
              renderContent(data)
            ) : Array.isArray(data) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {Array.from((() => {
                  const arr = Array.isArray(data) ? data : [];
                  // version/sub_version 単位でユニーク化し、version_dt が新しいものを採用
                  const m = new Map<string, any>();
                  for (const it of arr as any[]) {
                    const key = `${Number(it?.wr_id) || 0}|${Number(it?.version) || 0}|${Number(it?.sub_version) || 0}`;
                    const prev = m.get(key);
                    if (!prev) {
                      m.set(key, it);
                    } else {
                      const tb = Date.parse(String(it?.version_dt ?? ''));
                      const ta = Date.parse(String(prev?.version_dt ?? ''));
                      const nb = Number.isNaN(tb) ? -Infinity : tb;
                      const na = Number.isNaN(ta) ? -Infinity : ta;
                      if (nb >= na) m.set(key, it);
                    }
                  }
                  return m.values();
                })())
                  .sort((a: any, b: any) => {
                    const byVer = Number(b?.version ?? 0) - Number(a?.version ?? 0);
                    if (byVer !== 0) return byVer;
                    const bySub = Number(b?.sub_version ?? 0) - Number(a?.sub_version ?? 0);
                    if (bySub !== 0) return bySub;
                    const tb = Date.parse(b?.version_dt ?? '');
                    const ta = Date.parse(a?.version_dt ?? '');
                    // 新しい日時を先に
                    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
                  })
                  .map((item: any) => (
                    <Box
                      key={`${Number(item?.wr_id) || 0}-${Number(item?.version) || 0}-${Number(item?.sub_version) || 0}`}
                      sx={{ border: isDark ? '1px solid #334155' : '1px solid #eee', borderRadius: 1, p: 1.25, bgcolor: isDark ? '#1e293b' : '#fff' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 auto' }}>
                          version.{String(item?.version ?? '-')} ({formatVersionDt(item?.version_dt)})
                        </Typography>
                        <Tooltip title="このバージョンを削除" arrow>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deleting}
                              style={{ marginTop: 0}}
                              onClick={() => handleClickDelete(item?.wr_id, item?.version)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: '1 1 auto' }}>
                          {String(item?.title ?? '')}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpanded(makeKey(item))}
                          style={{ marginTop: 0 }}
                          aria-label={isExpanded(item) ? '折りたたむ' : '展開する'}
                          title={isExpanded(item) ? '折りたたむ' : '展開する'}
                        >
                          {isExpanded(item) ? (
                            <ArrowDropUpIcon fontSize="small" />
                          ) : (
                            <ArrowDropDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                      {isExpanded(item) && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {String(item?.body ?? '')}
                        </Typography>
                      )}
                    </Box>
                  ))}
              </Box>
            ) : (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: isDark ? '#0f172a' : '#f6f8fa',
                  color: isDark ? '#e2e8f0' : undefined,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  overflowX: 'auto',
                }}
              >
                {JSON.stringify(data, null, 2)}
              </Box>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              データがありません（wr_id: {wrId ?? '-'}）
            </Typography>
          )}
        </Box>

        <ConfirmDialog
          open={confirmOpen}
          title="バージョン削除"
          description={`${target?.version != null ? `バージョン${target?.version}` : 'このバージョン'}を削除します。よろしいですか？この操作は取り消せません。`}
          confirmText="削除"
          confirmColor="error"
          confirmVariant="contained"
          processing={deleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancel}
          onClose={handleCancel}
        />
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Button size="small" variant="outlined" onClick={onClose}>閉じる</Button>
        </Box>
      </Box>
    </Slide>
  );
};

export default VersionHistorySidebar;
